import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { 
  Utensils, ShoppingCart, Plus, Minus, ArrowLeft, X, 
  Check, Phone, CreditCard, MapPin, Clock, Receipt,
  ChevronRight, Search, Coffee
} from "lucide-react";
import { cn, formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEYS = {
  ORDER_STATE: (slug) => `dalabsom_${slug}_state`,
  CART: (slug) => `dalabsom_${slug}_cart`,
};

const CustomerOrder = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [step, setStep] = useState("type");
  const [orderType, setOrderType] = useState(null);
  const [tableNumber, setTableNumber] = useState("");
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCart, setShowCart] = useState(false);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [itemNotes, setItemNotes] = useState("");
  
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [customerPhone, setCustomerPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [proofDialog, setProofDialog] = useState(false);
  const [proofPayer, setProofPayer] = useState("");
  const [proofRef, setProofRef] = useState("");
  const [submittingProof, setSubmittingProof] = useState(false);

  const categoryRef = useRef(null);
  const brandColor = restaurant?.brand_color || "#0EA5E9";

  useEffect(() => {
    const qrTable = searchParams.get("table");
    if (qrTable) {
      setTableNumber(qrTable);
      setOrderType("eat_in");
      setStep("menu");
    }
    restoreOrderState();
  }, [searchParams]);

  useEffect(() => { fetchRestaurant(); }, [slug]);

  useEffect(() => {
    if (restaurant && cart.length > 0) {
      localStorage.setItem(STORAGE_KEYS.CART(slug), JSON.stringify(cart));
      localStorage.setItem(STORAGE_KEYS.ORDER_STATE(slug), JSON.stringify({ step, orderType, tableNumber }));
    }
  }, [cart, step, orderType, tableNumber, slug, restaurant]);

  const restoreOrderState = () => {
    try {
      const savedCart = localStorage.getItem(STORAGE_KEYS.CART(slug));
      const savedState = localStorage.getItem(STORAGE_KEYS.ORDER_STATE(slug));
      if (savedCart) { const p = JSON.parse(savedCart); if (p.length > 0) setCart(p); }
      if (savedState) {
        const p = JSON.parse(savedState);
        if (p.orderType) setOrderType(p.orderType);
        if (p.tableNumber) setTableNumber(p.tableNumber);
        if (savedCart && JSON.parse(savedCart).length > 0 && p.step !== "confirmation") setStep("menu");
      }
    } catch (e) { console.error("Error restoring order:", e); }
  };

  const clearOrderState = () => {
    localStorage.removeItem(STORAGE_KEYS.CART(slug));
    localStorage.removeItem(STORAGE_KEYS.ORDER_STATE(slug));
  };

  const fetchRestaurant = async () => {
    try {
      const [resR, menuR] = await Promise.all([
        axios.get(`${API}/public/restaurant/${slug}`),
        null
      ]);
      setRestaurant(resR.data);
      const menuResponse = await axios.get(`${API}/public/menu/${resR.data.id}`);
      setMenu(menuResponse.data);
      if (menuResponse.data.categories.length > 0) setActiveCategory(menuResponse.data.categories[0].id);
    } catch (err) { setError("Restaurant not found"); }
    finally { setLoading(false); }
  };

  const getItemImage = (item) => item.image_data || item.image_url;
  
  const calculateItemTotal = () => {
    if (!selectedItem) return 0;
    let total = selectedItem.price;
    if (selectedVariant) total += selectedVariant.price_modifier || 0;
    for (const extraId of selectedExtras) {
      const extra = selectedItem.extras?.find(e => e.id === extraId);
      if (extra) total += extra.price;
    }
    return total * itemQuantity;
  };

  const toggleExtra = (extraId) => {
    setSelectedExtras(prev => prev.includes(extraId) ? prev.filter(id => id !== extraId) : [...prev, extraId]);
  };

  const addToCart = () => {
    if (!selectedItem) return;
    const extrasDetails = selectedExtras.map(extraId => {
      const extra = selectedItem.extras?.find(e => e.id === extraId);
      return extra ? { id: extra.id, name: extra.name, price: extra.price } : null;
    }).filter(Boolean);
    const extrasTotal = extrasDetails.reduce((sum, e) => sum + e.price, 0);
    
    setCart([...cart, {
      id: `${selectedItem.id}-${selectedVariant?.id || "default"}-${selectedExtras.join("-")}-${Date.now()}`,
      menu_item_id: selectedItem.id,
      name: selectedItem.name,
      variant_id: selectedVariant?.id || null,
      variant_name: selectedVariant?.name || null,
      selected_extras: selectedExtras,
      extras_details: extrasDetails,
      extras_total: extrasTotal,
      price: selectedItem.price + (selectedVariant?.price_modifier || 0) + extrasTotal,
      quantity: itemQuantity,
      notes: itemNotes,
      image: getItemImage(selectedItem),
    }]);
    toast.success(`Added ${selectedItem.name}`);
    closeItemDialog();
  };

  const updateCartQuantity = (cartItemId, delta) => {
    setCart(cart.map(item => {
      if (item.id === cartItemId) {
        const newQty = Math.max(0, item.quantity + delta);
        return newQty === 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (cartItemId) => setCart(cart.filter(item => item.id !== cartItemId));

  const closeItemDialog = () => {
    setSelectedItem(null); setItemQuantity(1);
    setSelectedVariant(null); setSelectedExtras([]); setItemNotes("");
  };

  const getCartTotal = () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const getCartCount = () => cart.reduce((sum, item) => sum + item.quantity, 0);
  const getTotal = () => getCartTotal() * (1 + (restaurant?.vat_enabled ? restaurant.vat_percentage / 100 : 0));

  const handleSubmitOrder = async () => {
    if (cart.length === 0) { toast.error("Your cart is empty"); return; }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/public/orders/${restaurant.id}`, {
        order_type: orderType,
        table_number: orderType === "eat_in" ? tableNumber : null,
        items: cart.map(item => ({
          menu_item_id: item.menu_item_id, quantity: item.quantity,
          variant_id: item.variant_id, selected_extras: item.selected_extras || [], notes: item.notes,
        })),
        payment_method: paymentMethod,
        customer_phone: null,
      });
      setOrderResult(response.data); setStep("confirmation"); setCart([]); clearOrderState();
    } catch (err) { toast.error("Failed to place order. Please try again."); }
    finally { setSubmitting(false); }
  };

  const filteredItems = menu.items.filter(item => {
    const matchesCat = item.category_id === activeCategory;
    const matchesSearch = searchQuery === "" || item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  // ===== LOADING =====
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAF9F6" }}>
        <div className="w-10 h-10 border-3 rounded-full animate-spin" style={{ borderColor: `${brandColor}33`, borderTopColor: brandColor }} />
      </div>
    );
  }

  // ===== ERROR =====
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "#FAF9F6" }}>
        <Utensils className="w-16 h-16 text-stone-300 mb-4" />
        <h1 className="text-xl font-bold text-stone-800 mb-2">Restaurant Not Found</h1>
        <p className="text-stone-500 mb-6">The restaurant you're looking for doesn't exist.</p>
        <Button onClick={() => navigate("/")}>Go Home</Button>
      </div>
    );
  }

  // ===== ORDER TYPE SELECTION =====
  if (step === "type") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "#FAF9F6" }} data-testid="order-type-selection">
        <div className="w-full max-w-sm text-center">
          {restaurant.logo_data ? (
            <img src={restaurant.logo_data} alt={restaurant.name} className="h-24 w-24 rounded-3xl mx-auto mb-6 object-cover shadow-lg" />
          ) : (
            <div className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center shadow-lg" style={{ backgroundColor: brandColor }}>
              <Utensils className="w-12 h-12 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight mb-1">{restaurant.name}</h1>
          <p className="text-stone-500 mb-10">How would you like to order?</p>
          
          <div className="grid gap-4">
            <button
              className="group relative bg-white rounded-2xl p-6 border border-stone-200 hover:border-sky-300 hover:shadow-lg transition-all active:scale-[0.98] text-left flex items-center gap-4"
              onClick={() => { setOrderType("eat_in"); setStep(restaurant.enable_table_number ? "table" : "menu"); }}
              data-testid="order-eat-in"
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-amber-50 group-hover:bg-amber-100 transition-colors">
                <Utensils className="w-7 h-7 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-stone-900 text-lg">Eat In</p>
                <p className="text-sm text-stone-500">Dine at the restaurant</p>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-sky-500 transition-colors" />
            </button>
            <button
              className="group relative bg-white rounded-2xl p-6 border border-stone-200 hover:border-sky-300 hover:shadow-lg transition-all active:scale-[0.98] text-left flex items-center gap-4"
              onClick={() => { setOrderType("takeaway"); setStep("menu"); }}
              data-testid="order-takeaway"
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-sky-50 group-hover:bg-sky-100 transition-colors">
                <ShoppingCart className="w-7 h-7 text-sky-600" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-stone-900 text-lg">Takeaway</p>
                <p className="text-sm text-stone-500">Pick up your order</p>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-sky-500 transition-colors" />
            </button>
          </div>
          
          <p className="text-xs text-stone-400 mt-8">Powered by Dalabsom</p>
        </div>
      </div>
    );
  }

  // ===== TABLE NUMBER =====
  if (step === "table") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "#FAF9F6" }}>
        <div className="w-full max-w-sm text-center">
          <Button variant="ghost" className="absolute top-4 left-4 text-stone-600" onClick={() => setStep("type")}>
            <ArrowLeft className="w-5 h-5 mr-1" /> Back
          </Button>
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-sky-50">
            <MapPin className="w-10 h-10" style={{ color: brandColor }} />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight mb-1">Your Table</h1>
          <p className="text-stone-500 mb-8">We'll bring your order to you</p>
          <Input
            type="text" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)}
            placeholder="e.g., 12" className="text-center text-3xl h-16 mb-4 bg-white border-stone-200 rounded-2xl text-stone-900 font-bold"
            data-testid="table-number-input"
          />
          <Button className="w-full h-12 rounded-xl text-white text-lg font-semibold" style={{ backgroundColor: brandColor }}
            onClick={() => setStep("menu")} disabled={!tableNumber.trim()} data-testid="continue-to-menu">
            Continue to Menu
          </Button>
        </div>
      </div>
    );
  }

  // ===== MENU + CART VIEW =====
  if (step === "menu" || step === "cart") {
    return (
      <div className="min-h-screen pb-28" style={{ background: "#FAF9F6" }} data-testid="customer-menu">
        {/* ===== HEADER ===== */}
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-100">
          <div className="flex items-center gap-3 px-4 py-3">
            <Button variant="ghost" size="icon" className="h-9 w-9 text-stone-600 shrink-0"
              onClick={() => setStep(orderType === "eat_in" && restaurant.enable_table_number ? "table" : "type")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {restaurant.logo_data && (
              <img src={restaurant.logo_data} alt={restaurant.name} className="w-9 h-9 rounded-xl object-cover" />
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-stone-900 text-base truncate">{restaurant.name}</h1>
              <p className="text-xs text-stone-500">
                {orderType === "eat_in" ? `Table ${tableNumber}` : "Takeaway"}
              </p>
            </div>
            {cart.length > 0 && (
              <button className="relative" onClick={() => setShowCart(!showCart)} data-testid="view-cart-btn">
                <ShoppingCart className="w-6 h-6 text-stone-700" />
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                  {getCartCount()}
                </span>
              </button>
            )}
          </div>
          
          {/* ===== CATEGORY TABS ===== */}
          <div ref={categoryRef} className="flex gap-2 px-4 pb-3 overflow-x-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
            {menu.categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setShowCart(false); }}
                className={cn(
                  "shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all active:scale-95",
                  activeCategory === cat.id
                    ? "text-white shadow-md"
                    : "bg-white text-stone-600 border border-stone-200 hover:border-stone-300"
                )}
                style={activeCategory === cat.id ? { backgroundColor: brandColor } : {}}
                data-testid={`category-tab-${cat.id}`}
              >
                {cat.category_type === "drink" && <Coffee className="w-3 h-3 inline mr-1" />}
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* ===== CART PANEL (Slide down) ===== */}
        {showCart && (
          <div className="bg-white border-b border-stone-200 shadow-sm animate-in slide-in-from-top" data-testid="cart-panel">
            <div className="px-4 py-3 flex items-center justify-between border-b border-stone-100">
              <h3 className="font-bold text-stone-900">Your Order ({getCartCount()})</h3>
              <Button variant="ghost" size="sm" className="text-stone-500 h-7" onClick={() => setShowCart(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-stone-50">
              {cart.map(item => (
                <div key={item.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{item.name}</p>
                    {item.variant_name && <p className="text-xs text-stone-500">{item.variant_name}</p>}
                    {item.extras_details?.length > 0 && (
                      <p className="text-xs text-green-600">+{item.extras_details.map(e => e.name).join(", ")}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button className="w-7 h-7 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 active:scale-90"
                      onClick={() => updateCartQuantity(item.id, -1)}>
                      <Minus className="w-3 h-3 text-stone-600" />
                    </button>
                    <span className="w-6 text-center text-sm font-bold text-stone-900">{item.quantity}</span>
                    <button className="w-7 h-7 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 active:scale-90"
                      onClick={() => updateCartQuantity(item.id, 1)}>
                      <Plus className="w-3 h-3 text-stone-600" />
                    </button>
                  </div>
                  <p className="text-sm font-bold text-stone-900 w-16 text-right">{formatCurrency(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== MENU ITEMS ===== */}
        <div className="px-4 pt-4">
          <div className="divide-y divide-stone-100">
            {filteredItems.length === 0 ? (
              <div className="text-center py-16">
                <Utensils className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p className="text-stone-500 font-medium">No items in this category</p>
              </div>
            ) : (
              filteredItems.map(item => (
                <div
                  key={item.id}
                  className="flex gap-4 py-4 cursor-pointer active:bg-stone-50 transition-colors"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`menu-item-${item.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-stone-900 mb-0.5">{item.name}</h3>
                    {item.description && <p className="text-sm text-stone-500 line-clamp-2 mb-2">{item.description}</p>}
                    {item.extras?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {item.extras.slice(0, 3).map((e) => (
                          <span key={e.id || e.name} className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">
                            +{e.name}
                          </span>
                        ))}
                        {item.extras.length > 3 && <span className="text-xs text-stone-400">+{item.extras.length - 3} more</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg" style={{ color: brandColor }}>{formatCurrency(item.price)}</span>
                      {item.variants?.length > 0 && <span className="text-xs text-stone-400">from</span>}
                    </div>
                  </div>
                  {getItemImage(item) ? (
                    <img src={getItemImage(item)} alt={item.name} className="w-24 h-24 rounded-2xl object-cover bg-stone-100 shrink-0" />
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-stone-100 flex items-center justify-center shrink-0">
                      <Utensils className="w-8 h-8 text-stone-300" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ===== FLOATING CART BAR ===== */}
        {cart.length > 0 && (
          <div className="fixed bottom-6 left-4 right-4 z-50 max-w-md mx-auto" data-testid="cart-sticky-bar">
            <button
              className="w-full rounded-2xl p-4 flex items-center justify-between shadow-2xl text-white active:scale-[0.98] transition-transform"
              style={{ backgroundColor: brandColor }}
              onClick={() => setStep("checkout")}
              data-testid="proceed-checkout-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold">{getCartCount()} {getCartCount() === 1 ? "item" : "items"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">{formatCurrency(getCartTotal())}</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </button>
          </div>
        )}

        {/* ===== ITEM CUSTOMIZATION DIALOG ===== */}
        <Dialog open={!!selectedItem} onOpenChange={() => closeItemDialog()}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0 rounded-t-3xl sm:rounded-2xl">
            {selectedItem && (
              <>
                {getItemImage(selectedItem) ? (
                  <img src={getItemImage(selectedItem)} alt={selectedItem.name} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-32 bg-stone-100 flex items-center justify-center">
                    <Utensils className="w-12 h-12 text-stone-300" />
                  </div>
                )}
                <div className="p-5 space-y-5">
                  <div>
                    <h2 className="text-xl font-bold text-stone-900 tracking-tight">{selectedItem.name}</h2>
                    {selectedItem.description && <p className="text-stone-500 text-sm mt-1">{selectedItem.description}</p>}
                    <p className="text-2xl font-bold mt-2" style={{ color: brandColor }}>{formatCurrency(selectedItem.price)}</p>
                  </div>

                  {selectedItem.variants?.length > 0 && (
                    <div>
                      <Label className="text-stone-800 font-semibold text-sm mb-2 block">Choose an option</Label>
                      <RadioGroup value={selectedVariant?.id || ""} onValueChange={(v) => setSelectedVariant(selectedItem.variants.find(x => x.id === v))}>
                        <div className="space-y-2">
                          {selectedItem.variants.map(v => (
                            <label key={v.id} htmlFor={v.id}
                              className={cn("flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all",
                                selectedVariant?.id === v.id ? "border-sky-400 bg-sky-50" : "border-stone-200 bg-white hover:border-stone-300"
                              )}>
                              <RadioGroupItem value={v.id} id={v.id} />
                              <span className="flex-1 text-stone-900 font-medium">{v.name}</span>
                              {v.price_modifier > 0 && <span className="text-sm text-stone-500">+{formatCurrency(v.price_modifier)}</span>}
                            </label>
                          ))}
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {selectedItem.extras?.length > 0 && (
                    <div>
                      <Label className="text-stone-800 font-semibold text-sm mb-2 block">Add extras</Label>
                      <div className="space-y-2">
                        {selectedItem.extras.map(extra => (
                          <label key={extra.id}
                            className={cn("flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all",
                              selectedExtras.includes(extra.id) ? "border-green-400 bg-green-50" : "border-stone-200 bg-white hover:border-stone-300"
                            )} onClick={() => toggleExtra(extra.id)}>
                            <Checkbox checked={selectedExtras.includes(extra.id)} onCheckedChange={() => toggleExtra(extra.id)} />
                            <span className="flex-1 text-stone-900 font-medium">{extra.name}</span>
                            <span className="text-green-600 font-semibold text-sm">+{formatCurrency(extra.price)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-stone-800 font-semibold text-sm mb-2 block">Special instructions</Label>
                    <Textarea value={itemNotes} onChange={(e) => setItemNotes(e.target.value)}
                      placeholder="e.g., No onions, extra spicy" className="bg-white border-stone-200 rounded-xl resize-none" rows={2} />
                  </div>

                  {/* Quantity + Add to Cart */}
                  <div className="flex items-center gap-4 pt-2">
                    <div className="flex items-center gap-3 bg-stone-100 rounded-full px-2 py-1">
                      <button className="w-9 h-9 rounded-full bg-white border border-stone-200 flex items-center justify-center active:scale-90"
                        onClick={() => setItemQuantity(Math.max(1, itemQuantity - 1))}>
                        <Minus className="w-4 h-4 text-stone-700" />
                      </button>
                      <span className="w-6 text-center font-bold text-lg text-stone-900">{itemQuantity}</span>
                      <button className="w-9 h-9 rounded-full bg-white border border-stone-200 flex items-center justify-center active:scale-90"
                        onClick={() => setItemQuantity(itemQuantity + 1)}>
                        <Plus className="w-4 h-4 text-stone-700" />
                      </button>
                    </div>
                    <Button className="flex-1 h-12 rounded-xl text-white text-base font-bold active:scale-[0.98]"
                      style={{ backgroundColor: brandColor }}
                      onClick={addToCart} data-testid="add-to-cart-btn">
                      Add {formatCurrency(calculateItemTotal())}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== CHECKOUT =====
  if (step === "checkout") {
    return (
      <div className="min-h-screen pb-6" style={{ background: "#FAF9F6" }} data-testid="checkout-page">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-100 px-4 py-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-9 w-9 text-stone-600" onClick={() => setStep("menu")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-stone-900">Checkout</h1>
                <p className="text-xs text-stone-500">{orderType === "eat_in" ? `Table ${tableNumber}` : "Takeaway"} · {getCartCount()} items</p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Order Items */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-stone-100">
                <h3 className="font-bold text-stone-900 text-sm">Order Summary</h3>
              </div>
              <div className="divide-y divide-stone-50">
                {cart.map(item => (
                  <div key={item.id} className="px-4 py-3 flex items-center gap-3">
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button className="w-6 h-6 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50"
                        onClick={() => updateCartQuantity(item.id, -1)}><Minus className="w-3 h-3" /></button>
                      <span className="w-5 text-center text-sm font-bold text-stone-900">{item.quantity}</span>
                      <button className="w-6 h-6 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50"
                        onClick={() => updateCartQuantity(item.id, 1)}><Plus className="w-3 h-3" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{item.name}</p>
                      {item.extras_details?.length > 0 && (
                        <p className="text-xs text-stone-400 truncate">{item.extras_details.map(e => e.name).join(", ")}</p>
                      )}
                    </div>
                    <p className="text-sm font-bold text-stone-900">{formatCurrency(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-stone-200 space-y-1.5">
                <div className="flex justify-between text-sm text-stone-500">
                  <span>Subtotal</span><span className="text-stone-900">{formatCurrency(getCartTotal())}</span>
                </div>
                {restaurant.vat_enabled && restaurant.vat_percentage > 0 && (
                  <div className="flex justify-between text-sm text-stone-500">
                    <span>VAT ({restaurant.vat_percentage}%)</span>
                    <span className="text-stone-900">{formatCurrency(getCartTotal() * restaurant.vat_percentage / 100)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-1.5 border-t border-stone-100">
                  <span className="text-stone-900">Total</span>
                  <span style={{ color: brandColor }}>{formatCurrency(getTotal())}</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-stone-100">
                <h3 className="font-bold text-stone-900 text-sm">Payment Method</h3>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {restaurant.payment_methods?.includes("cash") && (
                  <PaymentOption selected={paymentMethod === "cash"} onClick={() => setPaymentMethod("cash")} brandColor={brandColor}
                    icon={<CreditCard className="w-5 h-5" />} label="Cash" testId="checkout-payment-method-cash" />
                )}
                {restaurant.payment_methods?.includes("pay_at_cashier") && (
                  <PaymentOption selected={paymentMethod === "pay_at_cashier"} onClick={() => setPaymentMethod("pay_at_cashier")} brandColor={brandColor}
                    icon={<Receipt className="w-5 h-5" />} label="Pay at Cashier" testId="checkout-payment-method-cashier" />
                )}
                {restaurant.payment_methods?.includes("pay_with_receipt") && (
                  <PaymentOption selected={paymentMethod === "pay_with_receipt"} onClick={() => setPaymentMethod("pay_with_receipt")} brandColor={brandColor}
                    icon={<Clock className="w-5 h-5" />} label="Pay Later" testId="checkout-payment-method-later" />
                )}
              </div>

              {paymentMethod === "pay_at_cashier" && (
                <div className="mx-4 mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-800">Your order will be sent to the kitchen. Pay at the cashier.</p>
                </div>
              )}
              {paymentMethod === "pay_with_receipt" && (
                <div className="mx-4 mb-4 p-3 bg-sky-50 rounded-xl border border-sky-200">
                  <p className="text-sm text-sky-800">The waiter will bring your receipt with payment instructions. Pay using any provider listed on the receipt.</p>
                </div>
              )}
            </div>

            {/* Place Order Button */}
            <Button className="w-full h-14 rounded-2xl text-white text-lg font-bold active:scale-[0.98] transition-transform shadow-lg"
              style={{ backgroundColor: brandColor }}
              onClick={handleSubmitOrder}
              disabled={submitting}
              data-testid="place-order-btn">
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Placing Order...
                </div>
              ) : `Place Order · ${formatCurrency(getTotal())}`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== CONFIRMATION =====
  if (step === "confirmation" && orderResult) {
    const isPaid = orderResult.payment_status === "paid";
    const isUnpaid = ["pay_at_cashier", "pay_with_receipt"].includes(orderResult.payment_method);
    
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "#FAF9F6" }} data-testid="order-confirmation">
        <div className="w-full max-w-sm text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${brandColor}15` }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brandColor}30` }}>
              <Check className="w-8 h-8" style={{ color: brandColor }} />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight mb-1">Order Placed!</h1>
          <p className="text-stone-500 mb-8">Your order is being prepared</p>

          <div className="bg-white rounded-2xl border border-stone-200 p-5 text-left space-y-3 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-stone-500">Order</span>
              <span className="font-mono font-bold text-stone-900">#{orderResult.order_number}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-500">Type</span>
              <span className="text-stone-900">{orderResult.order_type === "eat_in" ? `Table ${orderResult.table_number}` : "Takeaway"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-500">Total</span>
              <span className="font-bold" style={{ color: brandColor }}>{formatCurrency(orderResult.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-500">Status</span>
              <Badge className={cn("text-xs", isPaid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700")}>
                {isPaid ? "Paid" : isUnpaid ? "Pay Later" : "Pending"}
              </Badge>
            </div>
          </div>

          {isUnpaid && (
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 text-left mb-6 flex gap-3">
              <Receipt className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">Payment Required</p>
                <p className="text-sm text-amber-700">Pay {formatCurrency(orderResult.total)} at the cashier or on your phone.</p>
              </div>
            </div>
          )}

          {/* Quick actions: View Receipt / Call Waiter — always available on confirmation */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Button
              variant="outline"
              className="h-11"
              onClick={() => { window.location.href = `/track/${orderResult.id}${slug ? `?restaurant=${slug}` : ""}`; }}
              data-testid="view-receipt-btn"
            >
              <Receipt className="w-4 h-4 mr-2" />
              View Receipt
            </Button>
            {orderResult.order_type === "eat_in" && (
              <Button
                variant="outline"
                className="h-11 border-emerald-300 text-emerald-700"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/public/orders/${orderResult.id}/call-waiter`, { reason: null });
                    toast.success("Waiter notified");
                  } catch (e) { toast.error("Couldn't reach waiter"); }
                }}
                data-testid="call-waiter-confirm-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Waiter
              </Button>
            )}
          </div>

          {/* No payment options shown on confirmation — customer waits for cashier-printed
              receipt which has all USSD codes. Confirmation only shows Track Order + Call Waiter. */}

          <div className="flex items-center justify-center gap-2 text-stone-400 mb-6">
            <Clock className="w-4 h-4" /> <span className="text-sm">We'll notify you when ready</span>
          </div>

          <div className="space-y-3">
            <Button className="w-full h-12 rounded-xl text-white font-bold" style={{ backgroundColor: brandColor }}
              onClick={() => navigate(`/track/${orderResult.id}?restaurant=${slug}`)}>
              Track Order Status
            </Button>
            <Button variant="outline" className="w-full h-12 rounded-xl border-stone-200"
              onClick={() => { setStep("type"); setOrderResult(null); }}>
              Place Another Order
            </Button>
          </div>
          
          <p className="text-xs text-stone-400 mt-6">Powered by Dalabsom</p>
        </div>

        {/* Payment proof submission dialog */}
        <Dialog open={proofDialog} onOpenChange={setProofDialog}>
          <DialogContent className="max-w-md" data-testid="proof-dialog">
            <DialogHeader>
              <DialogTitle>Submit payment confirmation</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-stone-600">After paying, enter your details so the cashier can verify your payment.</p>
              {orderResult?.payment_unique_amount && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                  <p className="text-xs text-amber-700">Pay this exact amount:</p>
                  <p className="font-bold text-amber-900 font-mono">{formatCurrency(orderResult.payment_unique_amount)}</p>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-stone-700">Your phone number (sender) *</label>
                <input type="tel" value={proofPayer} onChange={(e) => setProofPayer(e.target.value)}
                  placeholder="+252 61 123 4567"
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  data-testid="proof-payer" />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-700">Transaction reference (optional)</label>
                <input type="text" value={proofRef} onChange={(e) => setProofRef(e.target.value)}
                  placeholder="e.g. EVC-12345"
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  data-testid="proof-ref" />
              </div>
              <Button className="w-full h-11 text-white font-bold"
                style={{ backgroundColor: brandColor }}
                disabled={submittingProof || !proofPayer.trim()}
                onClick={async () => {
                  if (!proofPayer.trim()) { toast.error("Phone number required"); return; }
                  setSubmittingProof(true);
                  try {
                    const payload = {
                      payer_number: proofPayer.trim(),
                      transaction_ref: proofRef.trim() || null,
                      paid_amount: orderResult.payment_unique_amount || orderResult.total,
                    };
                    const r = await axios.post(`${API}/orders/${orderResult.id}/payment/proof`, payload);
                    setOrderResult(r.data.order || r.data);
                    setProofDialog(false);
                    setProofPayer(""); setProofRef("");
                    toast.success("Payment confirmation sent — cashier will verify shortly");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to submit");
                  } finally { setSubmittingProof(false); }
                }}
                data-testid="proof-submit-btn">
                {submittingProof ? "Sending..." : "Submit Confirmation"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
};

// ===== PAYMENT OPTION CARD =====
const PaymentOption = ({ selected, onClick, icon, label, brandColor, testId }) => (
  <button
    className={cn(
      "flex flex-col items-center gap-2 p-3 rounded-xl border transition-all active:scale-95",
      selected ? "bg-sky-50 shadow-sm" : "bg-white border-stone-200 hover:border-stone-300"
    )}
    style={selected ? { borderColor: brandColor } : {}}
    onClick={onClick}
    data-testid={testId}
  >
    <div className={cn("transition-colors", selected ? "text-sky-600" : "text-stone-500")}>{icon}</div>
    <span className={cn("text-xs font-semibold", selected ? "text-stone-900" : "text-stone-600")}>{label}</span>
  </button>
);

export default CustomerOrder;
